/**
 * EliasShell — chat-first layout for the Elias edition.
 *
 * Mirrors apps/web/src/ministry/MinistryShell.tsx but stripped to the
 * essentials for a single-user edition:
 *   1. Edition guard — non-Elias users bounced to /
 *   2. Membership guard — RLS denies; if no workspace visible, show
 *      access-denied (covers the case where the seed migration ran but
 *      the membership row hasn't been added yet).
 *   3. Workspace isolation — force the active workspace to Elias on
 *      entry so all downstream chat / context queries scope to the
 *      Elias KG.
 *   4. Theme — Elias is theme-agnostic (no forced light mode like
 *      Mohap); whatever the user picked persists.
 *
 * No role guard: Elias has a single seeded role ("operator") and the
 * permission catalogue is intentionally thin. If a role taxonomy
 * materialises later, add a useEliasRole hook and gate here.
 *
 * Routes:
 *   /elias        → EliasChatPage (full-screen chat with prominent upload)
 */

import { Outlet, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { useEliasEdition } from "./hooks/useEliasEdition";
import { useEliasWorkspace } from "./hooks/useEliasWorkspace";
import { useWorkspace } from "@/contexts/WorkspaceContext";

function ShellLoader({ label }: { label: string }) {
  return (
    <div data-elias className="flex min-h-screen items-center justify-center bg-background">
      <span className="text-sm font-medium tracking-wider animate-pulse text-foreground/40">
        {label}
      </span>
    </div>
  );
}

export function EliasShell() {
  const editionConfig = useEliasEdition();
  const isElias = editionConfig.edition === "individual-elias";
  const { workspace, loading: wsLoading } = useEliasWorkspace();
  const { currentWorkspace, switchWorkspace, workspaces } = useWorkspace();

  // Workspace isolation: force the active workspace to Elias on entry
  // so all downstream chat queries scope to the Elias KG. Without this,
  // a user who also belongs to a personal workspace would leak personal
  // KG entities into the Elias chat.
  useEffect(() => {
    if (!workspace) return;
    const target = workspaces.find((w) => w.id === workspace.id);
    if (!target) return;
    if (currentWorkspace?.id !== workspace.id) {
      switchWorkspace(workspace.id);
    }
  }, [workspace, workspaces, currentWorkspace?.id, switchWorkspace]);

  if (!isElias) {
    return <Navigate to="/" replace />;
  }

  if (wsLoading) {
    return <ShellLoader label="ELIAS" />;
  }

  if (!workspace) {
    return (
      <div data-elias className="flex min-h-screen items-center justify-center px-6 bg-background">
        <div className="max-w-md text-center">
          <div className="mb-2 text-xs uppercase tracking-wider text-foreground/60">
            Access denied
          </div>
          <p className="text-sm leading-relaxed text-foreground/70">
            This workspace is not visible to your account. Contact the workspace
            owner to be added as a member.
          </p>
        </div>
      </div>
    );
  }

  // Hold rendering until WorkspaceContext has actually switched.
  if (currentWorkspace?.id !== workspace.id) {
    return <ShellLoader label="ELIAS" />;
  }

  // NChatProvider lives at the App root (May 2026) — see App.tsx. Mounting
  // it here would create a second WebSocket and double-fire chat events.
  return (
    <div data-elias className="flex min-h-screen flex-col bg-background text-foreground">
      <Outlet />
    </div>
  );
}
